/* @flow strict */

import * as React from 'react';
import { POST, DELETE, GET, PUT } from '/client/src/api';
import DataTable from 'react-data-table-component';
import Loader from '/client/src/components/Loader';
import SponsorSearchbar from './searchbar';
import { Link } from 'react-router-dom';
import SponsorsPageModal from './modal';
import { sponsorColumns, usersColumns } from './tableHeaders';
import CssPinner from '/client/src/components/csspinner';
import CreateUserModal from './userCreateModal';
import NavbarTitleBackAction from '/client/src/components/topNavbar/backAction';
import { DefaultContainerContext } from '/client/src/contexts/defaultContainer';
import _ from 'lodash';

type PropsType = {};

const Sponsors = ({}: PropsType): React.Element<'div'> => {
  const context = React.useContext(DefaultContainerContext);
  const sponsorListApiUrl = '/api/v1/1/sponsors';
  const [sponsors, setSponsors] = React.useState([]);
  const [listUsersSponsorDisplay, setListUserSponsorDisplay] = React.useState(null);
  const [sponsorUsers, setSponsorUsers] = React.useState([]);
  const [selectedSponsorRows, _handleSponsorSelectRow] = React.useState([]);
  const [selectedUsersRows, _handleUsersSelectRow] = React.useState([]);
  const [showModalCreate, setShowModalCreate] = React.useState(false);
  const [showModalEdit, setShowModalEdit] = React.useState(false);
  const [showModalUserAdd, setShowModalUserAdd] = React.useState(false);
  const [showModalUserEdit, setShowModalUserEdit] = React.useState(false);
  const customStyles = {
    headCells: {
      style: {
        fontWeight: 700,
        fontSize: '1rem'
      }
    }
  };
  const [newSponsor, setNewSponsor] = React.useState('');
  const [editSponsor, setEditSponsor] = React.useState('');
  const [newSponsorEmail, setNewSponsorEmail] = React.useState('');
  const [isPending, setPending] = React.useState(false);

  const handleSponsorSelectRow = (row: []) => {
    _handleSponsorSelectRow(row);
  };

  const handleUsersSelectRow = (row: []) => {
    _handleUsersSelectRow(row);
  };

  const handleLoaded = React.useCallback(
    async (error: Error, response: any) => {
      if (!error) {
        try {
          setSponsors(_.get(response, 'data.result', []));
        } catch (e) {
          console.error('can\'t get sponsor list');
        }
      }
    },
    []
  );

  const handleCreateSponsor = async () => {
    setPending(true);

    try {
      const { success } = await POST(sponsorListApiUrl, {
        name: newSponsor,
        emails: [newSponsorEmail]
      });

      if (success) {
        setShowModalCreate(false);
        setPending(false);
        await handleUpdateSponsorList();
        context.handleShowSystemDialog(true, {
          systemDialogType: 'success',
          systemDialogTitle: 'Great!',
          systemDialogContent: 'Sponsor was created'
        });
      }
    } catch (e) {
      console.log(e);

      context.handleShowSystemDialog(true, {
        systemDialogType: 'error',
        systemDialogTitle: 'Error!',
        systemDialogContent: 'Can\'t create sponsor'
      });
      setPending(false);
    }
  };

  const handleRemoveSponsor = async () => {
    if (!_.size(selectedSponsorRows)) {
      return;
    }

    CssPinner(document.querySelector('.EventPageDetailSponsor .panel-body'), true);

    try {
      const { success } = await DELETE(`${sponsorListApiUrl}/${selectedSponsorRows.id}`);

      if (success) {
        await handleUpdateSponsorList();
        _handleSponsorSelectRow([]);

        context.handleShowSystemDialog(true, {
          systemDialogType: 'success',
          systemDialogTitle: 'Great!',
          systemDialogContent: 'Sponsor was deleted'
        });
      }

      CssPinner(document.querySelector('.EventPageDetailSponsor .panel-body'), false);
    } catch (e) {
      CssPinner(document.querySelector('.EventPageDetailSponsor .panel-body'), false);
      console.log(e);

      context.handleShowSystemDialog(true, {
        systemDialogType: 'error',
        systemDialogTitle: 'Error!',
        systemDialogContent: 'Can\'t remove sponsor'
      });
    }
  };

  const handleEditSponsor = async () => {
    if (!_.size(selectedSponsorRows)) {
      return;
    }

    CssPinner(document.querySelector('.EventPageDetailSponsor .panel-body'), true);

    try {
      const { success } = await PUT(sponsorListApiUrl, {
        name: editSponsor,
        id: selectedSponsorRows.id
      });

      if (success) {
        _handleSponsorSelectRow([]);
        setShowModalEdit(false);
        await handleUpdateSponsorList();

        context.handleShowSystemDialog(true, {
          systemDialogType: 'success',
          systemDialogTitle: 'Great!',
          systemDialogContent: 'Sponsor was edited'
        });
      }

      CssPinner(document.querySelector('.EventPageDetailSponsor .panel-body'), false);
    } catch (e) {
      CssPinner(document.querySelector('.EventPageDetailSponsor .panel-body'), false);
      console.log(e);

      context.handleShowSystemDialog(true, {
        systemDialogType: 'error',
        systemDialogTitle: 'Error!',
        systemDialogContent: 'Can\'t edit sponsor'
      });
    }
  };

  const handleUpdateSponsorList = async () => {
    try {
      const { result } = await GET(sponsorListApiUrl);

      setSponsors(result);
    } catch (e) {
      console.log(e);
    }
  };

  const handleGetUsersList = async (sponsorId: number) => {
    CssPinner(document.querySelector('.EventPageDetailSponsor .panel-body'), true);

    try {
      const { result, success } = await GET(`${sponsorListApiUrl}/users/${sponsorId}`);

      if (success) {
        const selectedSponsor = sponsors[_.findIndex(sponsors, (item: {}): void => item.id === `${sponsorId}`)];

        setSponsorUsers(result);
        setListUserSponsorDisplay(selectedSponsor);
      }

      CssPinner(document.querySelector('.EventPageDetailSponsor .panel-body'), false);
    } catch (e) {
      console.log(e);
      CssPinner(document.querySelector('.EventPageDetailSponsor .panel-body'), false);
    }
  };

  const handleCreateUser = async (data: {}) => {
    setPending(true);

    try {
      const { success } = await POST(`${sponsorListApiUrl}/users`, {
        sponsor_id: listUsersSponsorDisplay.id,
        users: data
      });

      if (success) {
        setShowModalUserAdd(false);
        setPending(false);
        // setSponsorUsers([...sponsorUsers, ...data]); need id new user from back
        await handleGetUsersList(listUsersSponsorDisplay.id);
        await handleUpdateSponsorList();
        context.handleShowSystemDialog(true, {
          systemDialogType: 'success',
          systemDialogTitle: 'Great!',
          systemDialogContent: 'User was created'
        });
      }
    } catch (e) {
      console.log(e);

      context.handleShowSystemDialog(true, {
        systemDialogType: 'error',
        systemDialogTitle: 'Error!',
        systemDialogContent: 'Can\'t create user'
      });
      setPending(false);
    }
  };

  const handleEditUser = async (data: {}) => {
    setPending(true);

    try {
      const { success } = await PUT(`${sponsorListApiUrl}/users/update/${selectedUsersRows.id}`, {
       ...data
      });

      if (success) {
        setShowModalUserEdit(false);
        handleUsersSelectRow([]);
        setPending(false);
        // setSponsorUsers([...sponsorUsers, ...data]); need id new user from back
        await handleGetUsersList(listUsersSponsorDisplay.id);
        context.handleShowSystemDialog(true, {
          systemDialogType: 'success',
          systemDialogTitle: 'Great!',
          systemDialogContent: 'User was edited'
        });
      }
    } catch (e) {
      console.log(e);

      context.handleShowSystemDialog(true, {
        systemDialogType: 'error',
        systemDialogTitle: 'Error!',
        systemDialogContent: 'Can\'t edit user'
      });
      setPending(false);
    }
  };

  const handleRemoveUser = async () => {
    if (!_.size(selectedUsersRows)) {
      return;
    }

    CssPinner(document.querySelector('.EventPageDetailSponsor .panel-body'), true);

    try {
      const { success } = await DELETE(`${sponsorListApiUrl}/users`, {
        ids: [selectedUsersRows.id]
      });

      if (success) {
        await handleGetUsersList(listUsersSponsorDisplay.id);
        _handleUsersSelectRow([]);

        context.handleShowSystemDialog(true, {
          systemDialogType: 'success',
          systemDialogTitle: 'Great!',
          systemDialogContent: 'User was deleted'
        });
      }

      CssPinner(document.querySelector('.EventPageDetailSponsor .panel-body'), false);
    } catch (e) {
      CssPinner(document.querySelector('.EventPageDetailSponsor .panel-body'), false);
      console.log(e);

      context.handleShowSystemDialog(true, {
        systemDialogType: 'error',
        systemDialogTitle: 'Error!',
        systemDialogContent: 'Can\'t remove user'
      });
    }
  };

  return (
    <div className="sparxo-router-content">
      <NavbarTitleBackAction />
      <div className="sparxo-page-container">
        <div className="panel panel-default EventPageDetailSponsor">
          <div className="navbar-page-toolbar send-mail-navbar panel-heading-color3">
            <div className="container-fluid" style={{ height: '50px' }}>
              <ul className="nav top-nav">
                {!!listUsersSponsorDisplay ?
                  <li>
                    <span className="plant-text">{listUsersSponsorDisplay.name} Users</span>
                  </li>
                  :
                  <li
                    key={'EventPageDetailSponsor-menuItems-sponsors'}
                    className='active'
                  >
                    <Link to={'/sponsors'}>
                      Sponsors
                    </Link>
                  </li>
                }
              </ul>
            </div>
            {!!listUsersSponsorDisplay ?
              <SponsorSearchbar
                handleShowModalCreate={setShowModalUserAdd}
                handleRemove={() => {
                  context.handleShowSystemDialog(true, {
                    systemDialogType: 'warning',
                    systemDialogTitle: 'Hmmm...',
                    systemDialogContent: 'Remove the Sponsor LogIn?',
                    systemDialogCallback: async () => {
                      await handleRemoveUser();
                    }
                  });
                }}
                handleShowModalEdit={() => {
                  setShowModalUserEdit(true);
                }}
                handleGoBack={(): void => setListUserSponsorDisplay(null)}
                isEmptyList={!_.size(listUsersSponsorDisplay)}
                isEmptySelectedItems={!_.size(selectedUsersRows)}
                isUserList
              />
            :
              <SponsorSearchbar
                handleShowModalCreate={setShowModalCreate}
                handleRemove={() => {
                  context.handleShowSystemDialog(true, {
                    systemDialogType: 'warning',
                    systemDialogTitle: 'Hmmm...',
                    systemDialogContent: 'Remove the sponsor?',
                    systemDialogCallback: async () => {
                      await handleRemoveSponsor();
                    }
                  });
                }}
                handleShowModalEdit={() => {
                  setEditSponsor(selectedSponsorRows.name);
                  setShowModalEdit(true);
                }}
                isEmptyList={!_.size(sponsors)}
                isEmptySelectedItems={!_.size(selectedSponsorRows)}
              />
            }

          </div>

          <div className="panel-body">
            <div style={{ position: 'relative', minHeight: '120px' }}>
              <div style={{ width: '100%', overflowX: 'auto', position: 'relative' }}>
                <Loader
                  url={sponsorListApiUrl}
                  onLoaded={handleLoaded}
                >
                  {!listUsersSponsorDisplay ?
                    <DataTable
                      columns={sponsorColumns(handleSponsorSelectRow, selectedSponsorRows, handleGetUsersList)}
                      data={sponsors}
                      customStyles={customStyles}
                      Clicked
                      pagination={!!_.size(sponsors) > 10}
                      highlightOnHover
                      responsive
                      striped
                      fixedHeader
                      noHeader
                      sortIcon={(): React.Element<typeof React.Fragment> => {
                        return (
                          <>
                            <span className="-sort-desc" />
                            <span className="-sort-asc" />
                          </>
                        );
                      }}
                    />
                    :
                    <DataTable
                      columns={usersColumns(handleUsersSelectRow, selectedUsersRows)}
                      data={sponsorUsers}
                      customStyles={customStyles}
                      Clicked
                      pagination={!!_.size(sponsorUsers) > 10}
                      highlightOnHover
                      responsive
                      striped
                      fixedHeader
                      noHeader
                      sortIcon={(): React.Element<typeof React.Fragment> => {
                        return (
                          <>
                            <span className="-sort-desc" />
                            <span className="-sort-asc" />
                          </>
                        );
                      }}
                    />
                  }
                </Loader>
              </div>
            </div>

            {showModalCreate &&
              <SponsorsPageModal
                title={'Create sponsor'}
                inputNameValue={newSponsor}
                handleChangeInputName={setNewSponsor}
                inputEmailValue={newSponsorEmail}
                handleChangeInputEmail={setNewSponsorEmail}
                handleSubmit={handleCreateSponsor}
                handleClose={(): void => setShowModalCreate(false)}
                isOpen={showModalCreate}
                isPending={isPending}
                isCreate
              />
            }

            {showModalEdit &&
              <SponsorsPageModal
                title={'Edit sponsor name'}
                inputNameValue={editSponsor}
                handleChangeInputName={setEditSponsor}
                handleSubmit={handleEditSponsor}
                handleClose={(): void => setShowModalEdit(false)}
                isOpen={showModalEdit}
                isPending={isPending}
              />
            }

            {showModalUserAdd &&
              <CreateUserModal
                isOpen={showModalUserAdd}
                isPending={isPending}
                handleClose={(): void => setShowModalUserAdd(false)}
                handleSubmit={handleCreateUser}
              />
            }

            {showModalUserEdit &&
              <CreateUserModal
                isOpen={showModalUserEdit}
                isPending={isPending}
                handleClose={(): void => setShowModalUserEdit(false)}
                handleSubmit={handleEditUser}
                userToEdit={selectedUsersRows}
              />
            }
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sponsors;
